<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useCarinfoStore } from '@/stores/car-info'
import vinDecoder from '@/helpers/vinDecoder'
import newElement from '@/helpers/newElement'
import boundsFromCoords from '@/helpers/boundsFromCoords.js'

// Props
const props = defineProps({
    trip: {
        type: Object,
        required: true
    }
})

// Store
const carinfoStore = useCarinfoStore()
const translaterStore   = useTranslaterStore()

// Data
let map = null
const mapContainer = ref(null)
const mapCenter = ref([37.588144, 55.733842])
const mapZoom = ref(18)

// Computed
const getTranslate = computed(() => translaterStore.getTranslate)
const carVin = computed(() => carinfoStore.carinfo.data.vin)

// Инициализация карты
async function initYMap() {

    if(props.trip.coords.length == 1) mapCenter.value = props.trip.coords[0]
    
    const {
        YMap, 
        YMapDefaultSchemeLayer, 
        YMapDefaultFeaturesLayer, 
        YMapControls, 
        YMapScaleControl,
    } = ymaps3

    const {YMapZoomControl} = await ymaps3.import('@yandex/ymaps3-default-ui-theme')

    // установка зума
    if (localStorage.getItem('UTA_TRIPS_MAP_ZOOM_LVL')) {
        mapZoom.value = JSON.parse(localStorage.getItem('UTA_TRIPS_MAP_ZOOM_LVL'))
    }
    
    map = new YMap(mapContainer.value, {
            location: {
                bounds: boundsFromCoords(props.trip.coords),
            },
            margin: [100, 100, 100, 100]
        },
        [
            new YMapDefaultSchemeLayer({}),     // Слой для тайлов
            new YMapDefaultFeaturesLayer({})    // Слой для геообъектов на карте
        ]
    )

    // Масштаб
    const bLeftControl = new YMapControls({position: 'bottom left'}, [new YMapScaleControl({})])
    map.addChild(bLeftControl)
    
    // Кнопки управления зумом
    const rightControl = new YMapControls({position: 'right'})
    rightControl.addChild(new YMapZoomControl({}))
    map.addChild(rightControl)
}

function showMarkerBaloon(event) {
    if(
        event.target.className == 'marker-finish' || 
        event.target.className == 'marker-start'
    ) {
        const markerBallon = event.target.firstChild
        if(markerBallon.classList.contains('map-balloon')) {
            markerBallon.classList.toggle('showed')
        }
    }

    if(event.target.classList.contains('marker-car__icon')) {
        const markerBallon = event.target.nextSibling
        if(markerBallon.classList.contains('map-balloon')) {
            markerBallon.classList.toggle('showed')
        }
    }    
}

function closeBaloon(event) {
    const parentClasses = event.target.parentElement.classList
    
    if(parentClasses.contains('showed')) {
        parentClasses.remove('showed')
    }
}

// Рисование маршрута поездки
function renderTrip() {

    const { YMapFeature, YMapMarker } = ymaps3

    const trip = new YMapFeature({
        geometry: {
            type: 'LineString',
            coordinates: props.trip.coords
        },
        style: {
            stroke: [{ width: 3, color: '#FF0000' }]
        }
    })

    // Маркер трека СТАРТ [начало]
    const markerStartElement = newElement('div', 'marker-start')

    const markerStartBaloonElement      = newElement('div',  ['map-balloon', 'marker-car__balloon'])
    const markerStartBaloonTextElement  = newElement('span', 'map-balloon__text')
    const markerStartBaloonCloseElement = newElement('span', ['map-balloon__close', 'material-icons'], 'close')

    markerStartBaloonElement.appendChild(markerStartBaloonTextElement)
    markerStartBaloonElement.appendChild(markerStartBaloonCloseElement)
    markerStartElement.appendChild(markerStartBaloonElement)

    markerStartElement.addEventListener('click', showMarkerBaloon)
    markerStartBaloonCloseElement.addEventListener('click', closeBaloon)

    const markerStart = new YMapMarker({ 
        coordinates: props.trip.coords[0],
        zIndex: 10 
    }, markerStartElement)
    // Маркер трека СТАРТ [конец]

    // Маркер трека ФИНИШ [начало]
    const markerFinishElement = newElement('div', 'marker-finish')

    const markerFinishBaloonElement         = newElement('div',  ['map-balloon', 'marker-car__balloon'])
    const markerFinishBaloonTextElement     = newElement('span', 'map-balloon__text')
    const markerFinishBaloonCloseElement    = newElement('div', ['map-balloon__close', 'material-icons'], 'close')

    markerFinishBaloonElement.appendChild(markerFinishBaloonTextElement)
    markerFinishBaloonElement.appendChild(markerFinishBaloonCloseElement)
    markerFinishElement.appendChild(markerFinishBaloonElement)

    markerFinishElement.addEventListener('click', showMarkerBaloon)
    markerFinishBaloonCloseElement.addEventListener('click', closeBaloon)

    const markerFinish = new YMapMarker({ 
        coordinates: props.trip.coords[props.trip.coords.length - 1],
        zIndex: 10 
    }, markerFinishElement)
    // Маркер трека ФИНИШ [конец]

    markerStartBaloonTextElement.innerText = getTranslate.value.CARMAP_TRACK_START + ': ' + new Date(Number(props.trip.startedAt * 1000)).toLocaleString()
    markerFinishBaloonTextElement.innerText = getTranslate.value.CARMAP_TRACK_FINISH + ': ' + new Date(Number(props.trip.endedAt * 1000)).toLocaleString()

    map.addChild(trip)
    map.addChild(markerStart)
    map.addChild(markerFinish)
}

function renderCar() {
    const { YMapMarker } = ymaps3
    const vinDecode = vinDecoder(carVin.value)
    const modelName = vinDecode.name
    
    // Маркер автомобиля
    const markerCarElement = newElement('div', 'marker-car')

    const markerCarIconElement          = newElement('div', ['marker-car__icon', `marker-car__icon--${modelName}`])
    const markerCarBaloonElement        = newElement('div', ['map-balloon', 'marker-car__balloon'])
    const markerCarBaloonTextElement    = newElement('span', ['map-balloon__text', 'marker-car__balloon-text'])
    const markerCarBaloonCloseElement   = newElement('div', ['map-balloon__close', 'material-icons'], 'close')
    const markerCarBaloonSpeedElement   = newElement('div', 'marker-car__balloon-text-item')
    const markerCarBaloonDateElement    = newElement('div', 'marker-car__balloon-text-item')

    markerCarBaloonElement.appendChild(markerCarBaloonTextElement)
    markerCarBaloonElement.appendChild(markerCarBaloonCloseElement)
    markerCarElement.appendChild(markerCarIconElement)
    markerCarElement.appendChild(markerCarBaloonElement)

    markerCarBaloonSpeedElement.innerHTML = `<strong>${getTranslate.value.CARMAP_TRACK_START}:</strong><br />`
    markerCarBaloonSpeedElement.append(new Date(Number(props.trip.startedAt * 1000)).toLocaleString())

    markerCarBaloonDateElement.innerHTML = `<strong>${getTranslate.value.CARMAP_TRACK_FINISH}:</strong><br />`
    markerCarBaloonDateElement.append(new Date(Number(props.trip.endedAt * 1000)).toLocaleString())

    markerCarBaloonTextElement.append(markerCarBaloonSpeedElement)
    markerCarBaloonTextElement.append(markerCarBaloonDateElement)

    markerCarIconElement.addEventListener('click', showMarkerBaloon)
    markerCarBaloonCloseElement.addEventListener('click', closeBaloon)

    const markerCar = new YMapMarker({
        coordinates: props.trip.coords[0],
        // margin: [15, 26, 0, 0], 
        zIndex: 3 
    }, markerCarElement)

    map.addChild(markerCar)
}

onMounted(async()  => {
    // инициализировать карту
    await initYMap()

    // рисовать маршрут поездки
    if(props.trip.coords?.length > 1) renderTrip()
    if(props.trip.coords?.length == 1) renderCar()
})

onBeforeUnmount(() => {
    
    // Уничтожить карту 
    if(ymaps3 && map) {
        map.destroy()
        map = null
    }
})
</script>

<template>
    <div ref="mapContainer" class="uta-trips-map"></div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/car/car-trips-map.scss';
@import '@/assets/styles/scss/_balloon';
</style>